import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowRight,
  BarChart3,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ClipboardList,
  CookingPot,
  FlaskConical,
  Lock,
  User,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import SimpleTooltip from "@/components/ui/simple-tooltip";
import { trpc } from "@/hooks/trpc";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatDate } from "@/features/food-shared/sessions-table";
import {
  DEFAULT_THEME,
  type ProjectTheme,
  THEME_CONFIG,
} from "@/features/food-shared/project-themes";
import { FoodSessionId, FoodSessionStatusEnum } from "@brewbase/db/types";
import { SESSION_STATUS_CONFIG } from "@/features/food-shared/constants.ts";

export const Route = createFileRoute("/food-app/sessions/$session_id")({
  component: SessionJournalLayout,
  parseParams: (params) => ({
    session_id: parseInt(params.session_id, 10),
  }),
});

const STATUS_ORDER: FoodSessionStatusEnum[] = [
  FoodSessionStatusEnum.unstarted,
  FoodSessionStatusEnum.in_progress,
  FoodSessionStatusEnum.review,
  FoodSessionStatusEnum.completed,
];

const SECTIONS = [
  { id: "general", label: "General", icon: ClipboardList },
  { id: "setup", label: "Setup", icon: FlaskConical },
  { id: "running", label: "Running", icon: CookingPot },
  { id: "analysis", label: "Analysis", icon: FlaskConical },
  { id: "results", label: "Results", icon: BarChart3 },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

function getUnlockedSections(status: FoodSessionStatusEnum): Set<SectionId> {
  if (status === "completed")
    return new Set(["general", "setup", "running", "analysis", "results"]);
  if (status === "review")
    return new Set(["general", "setup", "running", "analysis"]);
  if (status === "in_progress")
    return new Set(["general", "setup", "running"]);
  return new Set(["general", "setup"]);
}

function getCompletedSections(status: FoodSessionStatusEnum): Set<SectionId> {
  if (status === "completed") return new Set(["general", "setup", "running", "analysis"]);
  if (status === "review") return new Set(["general", "setup", "running"]);
  if (status === "in_progress") return new Set(["general", "setup"]);
  return new Set();
}

const LOCK_REASON: Partial<Record<SectionId, string>> = {
  running: "Mark session as In Progress to unlock",
  analysis: "Complete the Running stage to unlock",
  results: "Complete Analysis to unlock",
};

function SessionJournalLayout() {
  const { session_id } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { data: session, isLoading } = trpc.food.sessions.get.useQuery(
    session_id as FoodSessionId,
  );

  const updateMutation = trpc.food.sessions.update.useMutation({
    onSuccess: () => {
      utils.food.sessions.get.invalidate(session_id);
      utils.food.sessions.list.invalidate();
      utils.food.sessions.listRecent.invalidate();
    },
    onError: (err) => toast.error(`Failed to update session: ${err.message}`),
  });

  const status = (session?.status ?? "unstarted") as FoodSessionStatusEnum;
  const unlockedSections = getUnlockedSections(status);
  const completedSections = getCompletedSections(status);

  const pathParts = location.pathname.split("/");
  const activeSection = (pathParts[4] ?? "general") as SectionId;

  useEffect(() => {
    if (session && !unlockedSections.has(activeSection)) {
      navigate({
        to: "/food-app/sessions/$session_id/general",
        params: { session_id: session_id as FoodSessionId },
        replace: true,
      });
    }
  }, [session, status, activeSection]);

  if (isLoading) {
    return (
      <div className="h-full flex overflow-hidden">
        <div className="w-[220px] shrink-0 border-r flex flex-col bg-background px-4 pt-5 space-y-3">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-5 w-24" />
          <div className="space-y-1.5 pt-2">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-3 w-28" />
          </div>
          <div className="border-t mt-1 -mx-4" />
          <div className="space-y-1 pt-1">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </div>
        <div className="flex-1 p-6 space-y-4">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <p className="text-sm font-medium">Session not found</p>
        <p className="text-xs text-muted-foreground">
          This session may have been deleted.
        </p>
      </div>
    );
  }

  const themeColor =
    THEME_CONFIG[
      (session.project_theme as ProjectTheme | undefined) ?? DEFAULT_THEME
    ].swatch;

  return (
    <div className="h-full flex overflow-hidden">
      <div className="w-[220px] shrink-0 border-r flex flex-col overflow-y-auto bg-background">
        <div className="px-4 pt-5 pb-4 space-y-3">
          <Link
            to="/food-app/projects/$project_id"
            params={{ project_id: session.project_id }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-3 w-3 shrink-0" />
            <span
              className="h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: themeColor }}
            />
            <span className="truncate">{session.project_name}</span>
          </Link>

          <div className="space-y-2">
            <div className="font-mono text-xl font-bold tracking-tight">
              {session.code}
            </div>
            <StatusSelector
              status={status}
              onChange={(s) =>
                updateMutation.mutate({
                  id: session_id as FoodSessionId,
                  status: s,
                })
              }
              isPending={updateMutation.isPending}
            />
          </div>

          <div className="space-y-1.5">
            {session.planned_date && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3 shrink-0" />
                <span>{formatDate(session.planned_date.toString())}</span>
              </div>
            )}
            {session.operator_name && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <User className="h-3 w-3 shrink-0" />
                <span className="truncate">{session.operator_name}</span>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Workflow</span>
              <span className="text-xs text-muted-foreground font-mono tabular-nums">
                {unlockedSections.size}/{SECTIONS.length}
              </span>
            </div>
            <div className="h-1 w-full bg-muted overflow-hidden">
              <div
                className={cn(
                  "h-full transition-all duration-300",
                  SESSION_STATUS_CONFIG[status].dotColor,
                )}
                style={{
                  width: `${(unlockedSections.size / SECTIONS.length) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>

        <div className="border-t mx-3" />

        <nav className="flex flex-col py-2">
          {SECTIONS.map((section) => {
            const isActive = activeSection === section.id;
            const isUnlocked = unlockedSections.has(section.id);
            const isDone = completedSections.has(section.id);

            const navContent = (
              <>
                <section.icon className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1">{section.label}</span>
                {isDone && !isActive && (
                  <Check className="w-3 h-3 text-farmless-green-8 shrink-0" />
                )}
                {!isUnlocked && (
                  <Lock className="w-3 h-3 opacity-40 shrink-0" />
                )}
              </>
            );

            const navClassName = cn(
              "flex items-center gap-2 px-3 py-3 text-sm transition-colors w-full text-left",
              isUnlocked
                ? isActive
                  ? "cursor-pointer bg-primary/10 text-primary font-medium"
                  : "cursor-pointer text-muted-foreground hover:text-foreground hover:bg-muted"
                : "cursor-not-allowed text-muted-foreground/40",
            );

            if (!isUnlocked) {
              const lockReason = LOCK_REASON[section.id];
              const lockedSpan = (
                <span className={navClassName}>{navContent}</span>
              );
              if (lockReason) {
                return (
                  <SimpleTooltip
                    key={section.id}
                    content={lockReason}
                    side="right"
                  >
                    {lockedSpan}
                  </SimpleTooltip>
                );
              }
              return <span key={section.id}>{lockedSpan}</span>;
            }

            return (
              <Link
                key={section.id}
                to={`/food-app/sessions/$session_id/${section.id}`}
                params={{ session_id: session_id as FoodSessionId }}
                className={navClassName}
              >
                {navContent}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <Outlet />
      </div>
    </div>
  );
}

function StatusSelector({
  status,
  onChange,
  isPending,
}: {
  status: FoodSessionStatusEnum;
  onChange: (status: FoodSessionStatusEnum) => void;
  isPending: boolean;
}) {
  const cfg = SESSION_STATUS_CONFIG[status] ?? SESSION_STATUS_CONFIG.unstarted;
  const currentIndex = STATUS_ORDER.indexOf(status);
  const nextStatus =
    currentIndex < STATUS_ORDER.length - 1
      ? STATUS_ORDER[currentIndex + 1]
      : null;
  const otherStatuses = STATUS_ORDER.filter(
    (s) => s !== status && s !== nextStatus,
  );

  const badge = (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded border font-medium px-2 py-0.5 text-xs cursor-pointer hover:opacity-80",
        cfg.color,
        cfg.bgColor,
        cfg.borderColor,
        isPending && "opacity-50 pointer-events-none",
      )}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full", cfg.dotColor)} />
      {cfg.label}
      <ChevronDown className="w-3 h-3 opacity-60" />
    </div>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isPending}>
        {badge}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Current: {cfg.label}
        </DropdownMenuLabel>

        {nextStatus && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onChange(nextStatus)}
              className="flex items-center gap-2"
            >
              <ArrowRight className="w-3.5 h-3.5 text-farmless-green-8" />
              <span>Mark as {SESSION_STATUS_CONFIG[nextStatus].label}</span>
            </DropdownMenuItem>
          </>
        )}

        {otherStatuses.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              Jump to status
            </DropdownMenuLabel>
            {otherStatuses.map((s) => (
              <DropdownMenuItem
                key={s}
                onClick={() => onChange(s)}
                className="flex items-center gap-2"
              >
                <span
                  className={cn(
                    "w-2 h-2 rounded-full",
                    SESSION_STATUS_CONFIG[s].dotColor,
                  )}
                />
                <span>{SESSION_STATUS_CONFIG[s].label}</span>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
